import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { apiService, PlanCapabilitiesResponse } from '../../services/api';

/** Maps tool category enum values to a human-readable label. */
const CATEGORY_LABELS: Record<string, string> = {
  function: 'Funktion',
  api: 'API',
  webhook: 'Webhook',
  mcp_server: 'MCP',
};

/**
 * Returns a Tailwind class-string that colours the Badge by tool category.
 * Mirrors the colour scheme used in ToolDefinitionsPage.
 */
function toolCategoryClasses(category: string): string {
  const map: Record<string, string> = {
    function: 'bg-blue-100 text-blue-800 border-transparent',
    api: 'bg-green-100 text-green-800 border-transparent',
    webhook: 'bg-purple-100 text-purple-800 border-transparent',
    mcp_server: 'bg-orange-100 text-orange-800 border-transparent',
  };
  return map[category] ?? 'bg-gray-100 text-gray-800 border-transparent';
}

interface PlanCapabilitiesPreviewProps {
  planId: string;
}

/**
 * Read-only preview of the features and tools that are activated for a
 * subscription plan. Renders two compact badge rows — no edit controls.
 *
 * Data is fetched once on mount when the planId is provided.
 */
export function PlanCapabilitiesPreview({ planId }: PlanCapabilitiesPreviewProps) {
  const [data, setData] = useState<PlanCapabilitiesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    setLoading(true);
    void apiService
      .getPlanCapabilities(planId)
      .then(setData)
      .catch(() => { setError(true); })
      .finally(() => { setLoading(false); });
  }, [planId]);

  if (loading) {
    return (
      <div className="space-y-2 pt-1" aria-busy="true" aria-label="Fähigkeiten werden geladen">
        <Skeleton className="h-3 w-16" />
        <div className="flex flex-wrap gap-1">
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
        <Skeleton className="h-3 w-12 mt-1" />
        <div className="flex flex-wrap gap-1">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="flex items-center gap-1 text-xs text-destructive pt-1" role="alert">
        <Loader2 className="h-3 w-3" aria-hidden="true" />
        Fähigkeiten konnten nicht geladen werden.
      </p>
    );
  }

  if (!data) return null;

  const hasFeatures = data.features.length > 0;
  const hasTools = data.tools.length > 0;

  return (
    <div className="space-y-2 pt-1">
      {/* Features section */}
      <div>
        <p className="mb-1 text-xs font-medium text-muted-foreground">Features</p>
        {hasFeatures ? (
          <div className="flex flex-wrap gap-1" role="list" aria-label="Aktivierte Features">
            {data.features.map(feature => (
              <Badge
                key={feature.name}
                variant="secondary"
                className="text-xs"
                title={feature.description}
                role="listitem"
              >
                {feature.displayName}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">Keine aktiviert</p>
        )}
      </div>

      {/* Tools section */}
      <div>
        <p className="mb-1 text-xs font-medium text-muted-foreground">Tools</p>
        {hasTools ? (
          <div className="flex flex-wrap gap-1" role="list" aria-label="Aktivierte Tools">
            {data.tools.map(tool => (
              <Badge
                key={tool.toolDefinitionId}
                variant="outline"
                className={`text-xs ${toolCategoryClasses(tool.category)}`}
                title={`${CATEGORY_LABELS[tool.category] ?? tool.category}: ${tool.name}`}
                role="listitem"
              >
                {tool.displayName}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">Keine aktiviert</p>
        )}
      </div>
    </div>
  );
}
